import { useState, useEffect, useCallback, useRef } from 'react';
import cx from 'classnames';
import formatPrice from '@bwoty-web/price-formatter';
import PropTypes from 'prop-types';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import { TextBox, Tags } from '@bwoty-web/styleguide';
import { Button } from '@bwoty-web/ui-kit';
import Titles from './components/titles';
import Ratings from './components/ratings';
import useIsClient from '../../hooks/useIsClient';
import {
  validateRedirectUrl,
  updateUrlWithOpenHotelSection,
} from '../../utils/url';
import {
  isVerticalTopPositionPastElement,
  isVerticalBottomPositionPastElement,
} from '../../utils/elements';
import Overview from './sections/overview/overview';
import AboutHotel from './sections/aboutHotel/aboutHotel';
import Navigation from './components/navigation';
import RoomTypePopUp from './sections/accommodation/roomTypePopup';
import SectionContentContainer from './sections/sectionContentContainer';
import queryHelper from '../../utils/url/queryHelper';
import { getRoomTypeInfoByWvIdAndRoomTypeKey } from '../../services/HotelsService';
import './hotelInfoView.scss';
import { scrollToSectionStart } from '../../utils/scroll';

const HotelInfoView = ({
  hotelContent,
  imageContent,
  labels,
  siteSettings,
  isConceptHotel,
  isCharterCombination,
  initialSelectedSection,
  departureMonth,
  hotelPrice,
  isMobile,
  popUpPage,
}) => {
  const isFirstRender = useRef(true);
  const horizontalScrollRef = useRef(null);
  const [selectedSectionKey, setSelectedSectionKey] = useState(
    initialSelectedSection,
  );
  const [openRoomTypeKey, setOpenRoomTypeKey] = useState();

  const [openedRoomTypeDataDictionary, updateOpenedRoomTypeDataDictionary] =
    useState({});

  const [stickyCTAIsVisible, setStickyCTAIsVisible] = useState(false);
  const hotelNavigationElementRef = useRef(null);

  const handleScroll = useCallback(() => {
    const hotelInfoTopContainer = document.getElementsByClassName(
      'gw-hotel-info-ratings',
    )[0];
    const isPastHotelInfoTopContainer =
      hotelInfoTopContainer &&
      isVerticalTopPositionPastElement(hotelInfoTopContainer);
    const priceMatrixHeading = document.getElementById('price-list');
    const isPastPriceMatrixHeading =
      priceMatrixHeading &&
      isVerticalBottomPositionPastElement(priceMatrixHeading);

    setStickyCTAIsVisible(
      isMobile && !isPastPriceMatrixHeading && isPastHotelInfoTopContainer,
    );
  }, [stickyCTAIsVisible]);

  useEffect(() => {
    const hotelInfoTopContainer = document.getElementsByClassName(
      'gw-hotel-info-ratings',
    )[0];
    const isPastHotelInfoTopContainer =
      hotelInfoTopContainer &&
      isVerticalTopPositionPastElement(hotelInfoTopContainer);

    const priceMatrixHeading = document.getElementById('price-list');
    const isPastPriceMatrixHeading =
      priceMatrixHeading &&
      isVerticalBottomPositionPastElement(priceMatrixHeading);
    if (isMobile && !isPastPriceMatrixHeading && isPastHotelInfoTopContainer) {
      setStickyCTAIsVisible(true);
    }
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const sectionKeyChangeHandler = (key) => {
    const isSameAsCurrentSection = key === selectedSectionKey;
    setSelectedSectionKey(key);

    if (isSameAsCurrentSection) {
      scrollToSectionStart();
      horizontalScrollRef.current?.updateScrollPosition();
    }
  };

  useEffect(() => {
    updateUrlWithOpenHotelSection(selectedSectionKey);
    if (isFirstRender.current) isFirstRender.current = false;
    else scrollToSectionStart();
  }, [selectedSectionKey]);

  const {
    name,
    geographical,
    ratings,
    tags,
    sections,
    meta,
    nonConceptAccommodation,
    facts,
  } = hotelContent;

  const { baseUrl, siteId } = siteSettings;
  const { wvId } = meta;

  const isClient = useIsClient();
  let decodedCombinationUrl;
  if (isClient && isCharterCombination) {
    const currentUrlString = window.location.href;
    const currentUrl = new URL(currentUrlString);
    const encodedCombinationUrl = currentUrl.searchParams.get('chooseUrl');

    decodedCombinationUrl = decodeURI(encodedCombinationUrl);
    const redirectUrlIsValid = validateRedirectUrl(
      currentUrlString,
      decodedCombinationUrl,
    );
    if (!redirectUrlIsValid) {
      throw new Error('The redirect url is not allowed!!');
    }
  }

  const buttonUrl = !isCharterCombination
    ? '#price-list'
    : decodedCombinationUrl;

  const toggleRoomInfo = useCallback(
    (key) => {
      if (key && !openedRoomTypeDataDictionary[key]) {
        if (isConceptHotel) {
          getRoomTypeInfoByWvIdAndRoomTypeKey(baseUrl, siteId, wvId, key).then(
            (res) => {
              updateOpenedRoomTypeDataDictionary({
                ...openedRoomTypeDataDictionary,
                [key]: res,
              });
            },
          );
        } else {
          const parseKey = parseInt(key, 10);
          const roomType = nonConceptAccommodation?.roomTypes?.find(
            (r) =>
              (r.wvId && r.wvId === parseKey) ||
              (r.externalId && r.externalId === key),
          );
          if (!roomType) return;
          updateOpenedRoomTypeDataDictionary({
            ...openedRoomTypeDataDictionary,
            [key]: roomType,
          });
        }
      }
      queryHelper.updateQueryWithValues({ roomTypeKey: key ? `${key}` : null });
      setOpenRoomTypeKey(`${key}`);
    },
    [
      openedRoomTypeDataDictionary,
      isConceptHotel,
      baseUrl,
      siteId,
      wvId,
      nonConceptAccommodation?.roomTypes,
    ],
  );

  useEffect(() => {
    const key =
      decodeURIComponent(queryHelper.getQueryKeyValue('roomTypeKey')) || 0;
    toggleRoomInfo(key);
  }, [toggleRoomInfo]);

  const messageEventHandler = (event) => {
    if (event.origin === window.origin) {
      if (typeof event.data === 'string') {
        const message = event.data.split(':');
        if (message[0] === 'cmd.open.popup' && message.length > 1) {
          toggleRoomInfo(message[1]);
        }
      }
    }
  };

  // In order to be able to open hotelPushes and the popup from the pricematrix app
  /* eslint-disable consistent-return */
  useEffect(() => {
    if (typeof window !== 'undefined' && window) {
      window.addEventListener('message', messageEventHandler);

      return () => window.removeEventListener('message', messageEventHandler);
    }
  }, [toggleRoomInfo]);

  const [selectedSection, selectedSubSection] = selectedSectionKey.split(':');
  const sectionHeadingDict = Object.fromEntries(
    sections.map((x) => [x.key, x.heading]),
  );
  const isGlobe = useIsGlobetrotter();

  return (
    <>
      <div className="gw-hotel-info__top-container">
        <TextBox className="gw-hotel-info__top-box">
          {tags?.length > 0 && (
            <Tags className="gw-hotel-info__tags" tags={tags} responsive />
          )}
          <div className="gw-hotel-info__top-wrapper">
            <Titles
              siteId={siteSettings.siteId}
              geographical={geographical}
              rating={ratings.standard}
              hotelName={name}
              tags={tags}
              usps={facts?.usps}
              hotelStandardText={labels?.hotelStandardText}
            />
            <div className="gw-hotel-info__top-right-wrapper">
              {hotelPrice && (
                <div className="gw-hotel-info__price-wrapper">
                  <span className="gw-hotel-info__price-label">
                    {labels?.hotelPrice}
                  </span>
                  <span
                    className={cx('gw-hotel-info__price', {
                      'gw-hotel-info__price--red': ![1, 18].includes(siteId),
                    })}
                  >
                    {formatPrice(hotelPrice, siteId, true)}
                  </span>
                </div>
              )}
              <div
                className={cx('gw-hotel-info__availability-button-container', {
                  'gw-hotel-info__availability-button-container--no-price':
                    !hotelPrice,
                })}
              >
                {!popUpPage && (
                  <Button
                    id="gw-hotel-info__see-availability-button"
                    className={cx('gw-hotel-info__see-availability-button', {
                      'gw-hotel-info__see-availability-button--no-price':
                        !hotelPrice,
                    })}
                    variant="primary"
                    href={buttonUrl}
                  >
                    {isCharterCombination
                      ? labels.charterCombinationBtn
                      : labels?.seeAvailabilityBtnText}
                  </Button>
                )}
                <Ratings
                  changeSection={sectionKeyChangeHandler}
                  ratings={ratings}
                  labels={labels}
                  siteSettings={siteSettings}
                />
              </div>
            </div>
          </div>
        </TextBox>
      </div>
      <TextBox
        className={cx('gw-hotel-info-container', {
          'gw-hotel-info-container--concept': isConceptHotel,
          'gw-hotel-info--globe': isGlobe,
        })}
      >
        <div
          className="gw-hotel-info"
          id="gw-hotel-info-top"
          itemScope
          itemType="https://schema.org/Hotel"
        >
          <div className="gw-hotel-info__nav-container">
            <a href="#gw-hotel-info-content" className="visible-hidden">
              skip links
            </a>
            <Navigation
              sections={sections}
              selectedSection={selectedSection}
              selectedSectionKey={selectedSectionKey}
              changeSection={sectionKeyChangeHandler}
              labels={labels}
              horizontalScrollRef={horizontalScrollRef}
            />
          </div>
          <div
            ref={hotelNavigationElementRef}
            className="gw-hotel-info__main-container"
            id="gw-hotel-info-content"
          >
            <Overview
              hotelContent={hotelContent}
              imageContent={imageContent}
              labels={labels}
              siteSettings={siteSettings}
              changeSection={sectionKeyChangeHandler}
              isActive={selectedSection === 'overview'}
              isConceptHotel={isConceptHotel}
              sectionHeadingDict={sectionHeadingDict}
              departureMonth={departureMonth}
            />
            <AboutHotel
              isActive={selectedSection === 'aboutHotel'}
              hotelContent={hotelContent}
              siteSettings={siteSettings}
              labels={labels}
            />
            {sections.map((section) => {
              return (
                <SectionContentContainer
                  key={section.key}
                  section={section}
                  selectedSubSection={
                    selectedSubSection && parseInt(selectedSubSection, 10)
                  }
                  siteSettings={siteSettings}
                  wvId={wvId}
                  isActive={selectedSection === section.key}
                  toggleRoomInfo={toggleRoomInfo}
                  changeSelectedSection={sectionKeyChangeHandler}
                  nonConceptAccommodation={nonConceptAccommodation}
                />
              );
            })}
          </div>
          {openRoomTypeKey && openedRoomTypeDataDictionary[openRoomTypeKey] && (
            <RoomTypePopUp
              isConceptHotel={isConceptHotel}
              imageDomain={siteSettings.imageDomain}
              generalUspList={nonConceptAccommodation.allRoomsUspLists[0]}
              roomType={openedRoomTypeDataDictionary[openRoomTypeKey]}
              accommodationSection={sections.find(
                (section) => section.key === 'accommodation',
              )}
              toggleRoomInfo={toggleRoomInfo}
            />
          )}
          {stickyCTAIsVisible && (
            <div className="gw-hotel-info__sticky-cta-mobile">
              <Button variant="primary" href={buttonUrl}>
                {labels?.seeAvailabilityBtnText}
              </Button>
            </div>
          )}
        </div>
      </TextBox>
    </>
  );
};

HotelInfoView.propTypes = {
  hotelContent: PropTypes.shape({
    name: PropTypes.string,
    facts: PropTypes.shape({
      all: PropTypes.arrayOf(PropTypes.shape({})),
      usps: PropTypes.arrayOf(PropTypes.string),
    }),
    geographical: PropTypes.shape({}),
    ratings: PropTypes.shape({
      standard: PropTypes.number,
      guests: PropTypes.number,
      ratingDetails: PropTypes.shape({
        numberOfAnswers: PropTypes.number,
      }),
      tripAdvisor: PropTypes.shape({
        numberOfReviews: PropTypes.number,
        value: PropTypes.number,
      }),
      officialRating: PropTypes.string,
    }),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
      }),
    ),
    isCharterCombination: PropTypes.bool,
    sections: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    selectedSection: PropTypes.string,
    meta: PropTypes.shape({
      wvId: PropTypes.string,
    }),
    imageList: PropTypes.arrayOf(PropTypes.shape).isRequired,
    nonConceptAccommodation: PropTypes.shape({
      roomTypes: PropTypes.arrayOf(PropTypes.shape({})),
      allRoomsUspLists: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  imageContent: PropTypes.shape({
    easyTravel: PropTypes.shape({}),
    conceptImgSrc: PropTypes.string,
  }).isRequired,
  siteSettings: PropTypes.shape({
    siteId: PropTypes.number,
    baseUrl: PropTypes.string,
    imageDomain: PropTypes.string,
  }).isRequired,
  labels: PropTypes.shape({
    seeAvailabilityBtnText: PropTypes.string.isRequired,
    hotelStandardText: PropTypes.string,
    hotelPrice: PropTypes.string.isRequired,
    charterCombinationBtn: PropTypes.string.isRequired,
  }).isRequired,
  isCharterCombination: PropTypes.bool,
  isConceptHotel: PropTypes.bool,
  initialSelectedSection: PropTypes.string,
  departureMonth: PropTypes.number.isRequired,
  hotelPrice: PropTypes.string,
  popUpPage: PropTypes.bool,
  isMobile: PropTypes.bool,
};

HotelInfoView.defaultProps = {
  isCharterCombination: false,
  isConceptHotel: false,
  initialSelectedSection: 'overview',
  hotelPrice: '',
  isMobile: false,
  popUpPage: false,
};

export default HotelInfoView;
